import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { Check, Info, X } from 'lucide-react'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

export interface ToastMessage {
  id: number
  text: string
  tone: 'ok' | 'info' | 'danger'
}

interface ToastValue {
  show: (text: string, tone?: ToastMessage['tone']) => void
}

const ToastContext = createContext<ToastValue>({ show: () => {} })

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const dismiss = useCallback((id: number) => {
    setMessages((current) => current.filter((message) => message.id !== id))
  }, [])

  const show = useCallback<ToastValue['show']>(
    (text, tone = 'ok') => {
      const id = Date.now() + Math.random()
      setMessages((current) => [...current, { id, text, tone }])
      window.setTimeout(() => dismiss(id), 4000)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex flex-col items-center gap-2"
        role="status"
        aria-live="polite"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              'gl-h pointer-events-auto flex items-center gap-2.5 rounded-full py-2.5 ps-4 pe-2.5',
              'text-[13px] font-semibold text-dark',
            )}
          >
            <span className="z flex items-center gap-2.5">
              <span
                className={cn(
                  'flex size-4 items-center justify-center',
                  message.tone === 'danger'
                    ? 'text-danger'
                    : message.tone === 'info'
                      ? 'text-accent-dark'
                      : 'text-success',
                )}
                aria-hidden="true"
              >
                {message.tone === 'info' ? <Info className="size-4" /> : <Check className="size-4" />}
              </span>
              {message.text}
              <button
                type="button"
                onClick={() => dismiss(message.id)}
                aria-label={t('act.dismiss')}
                className="flex size-6 items-center justify-center rounded-full text-muted hover:text-dark"
              >
                <X className="size-3.5" />
              </button>
            </span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastValue {
  return useContext(ToastContext)
}
